import { Injectable } from "@angular/core";
import { ModalController } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import moment from "moment";
import { BehaviorSubject, Observable, Subscription } from "rxjs";
import { GridPopupAlarmComponent } from "src/components/popup/grid-popup-alarm/grid-popup-alarm.component";
import { PopupAlarmComponent } from "src/components/popup/popup-alarm/popup-alarm.component";
import { PopupChangePasswordComponent } from "src/components/popup/popup-change-password/popup-change-password.component";
import { PopupContentAddressMethodsComponent } from "src/components/popup/popup-content-address-methods/popup-content-address-methods.component";
import { PopupContentListNotificationComponent } from "src/components/popup/popup-content-list-notification/popup-content-list-notification.component";
import { PopupFastCheckinComponent } from "src/components/popup/popup-fast-checkin/popup-fast-checkin.component";
import { PopupRequestNumericPasswordComponent } from "src/components/popup/popup-request-numeric-password/popup-request-numeric-password.component";
import { Address } from "src/components/shared-components/class/component-classes/address";
import { NotificationRequest_ShowPopup } from "src/model/class/general classes/notification-request";
import { ClickActionType } from "src/model/enum/enumCustomInterface";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { NotificationObjectKind } from "src/model/enum/enumNotifications";
import { AddressMethod } from "src/components/shared-components/interfaces/address-method";
import { AlertService } from "./alert.service";
import { LoggerService } from "./logger.service";
import { PopupAssociateExistingSinapsiDevice } from "src/components/popup/popup-associate-existing-sinapsi-device/popup-associate-existing-sinapsi-device";
import { iDevicesKnown } from "src/model/interface/iDevicesKnown";
import { PopupConnectBoxManagementComponent } from "src/components/popup/popup-connect-box-management/popup-connect-box-management.component";
import { iConnectBoxConfigurationResponse } from "src/model/interface/iConnectBoxConfigurationResponse";
import { Device } from "src/components/shared-components/class/general-classes/device";

@Injectable({
  providedIn: "root",
})
export class PopupService {
  private _logContext: string = "PopupService";

  private _modal: HTMLIonModalElement = null;
  private _alarmInfo: BehaviorSubject<NotificationRequest_ShowPopup> =
    new BehaviorSubject<NotificationRequest_ShowPopup>(null);
  private _update: Observable<NotificationRequest_ShowPopup> =
    this._alarmInfo.asObservable();
  private _lblRead: string = "Leggi";
  private _lbltitleModal: string = "Operazione";
  private _lblSubtitleModal: string =
    "Seleziona l'operazione da effettuare sull'indirizzo";
  private _translateSubscription: Subscription = null;
  private _lblWrite: string = "Scrivi";
  private _lblWriteAddress: string = "Scrittura indirizzo";
  private _lblValueToWrite: string = "Valore da scrivere";

  constructor(
    private _modalController: ModalController,
    private _loggerService: LoggerService,
    private _translateService: TranslateService,
    private _alertService: AlertService
  ) {
    this.loadTranslation();
    this.subscribeTranslationEvent();
  }

  //#region Translation
  public loadTranslation(): void {
    this._translateService
      .get([
        "dictionary.operation",
        "dictionary.operationOnAddress",
        "dictionary.read",
        "dictionary.write",
        "dictionary.writeAddress",
        "dictionary.valueToWrite",
      ])
      .subscribe((translation) => {
        this._lbltitleModal = translation["dictionary.operation"];
        this._lblSubtitleModal = translation["dictionary.operationOnAddress"];
        this._lblRead = translation["dictionary.read"];
        this._lblWrite = translation["dictionary.write"];
        this._lblWriteAddress = translation["dictionary.writeAddress"];
        this._lblValueToWrite = translation["dictionary.valueToWrite"];
      });

    moment.locale(this._translateService.getCurrentLang());
  }

  public subscribeTranslationEvent() {
    this._translateSubscription = this._translateService.onLangChange.subscribe(
      () => {
        this.loadTranslation();
      }
    );
  }
  //#endregion

  /**
   * Handle show modal to request numeric password.
   * It return promise that handle correct onr incorrect password
   *
   * @param passwordRight Password righ
   * @param customTitle Custom title to apply to component
   * @param autoCloseSecondsTimeout Handle auto close component. If it set to 0, timeout is ignored. Value is expressed in seconds
   * @returns
   */
  public async showModalRequestNumericPassword(
    passwordRight: string = "1234",
    customTitle: string = "",
    autoCloseSecondsTimeout: number = 0
  ): Promise<any> {
    const modal = await this._modalController.create({
      component: PopupRequestNumericPasswordComponent,
      cssClass: ["numeric-keypad-component"],
      componentProps: {
        correctPassword: passwordRight,
        autoCloseSecondsTimeout: autoCloseSecondsTimeout,
        //autoCloseSecondsTimeout: 0,
        titleText: customTitle,
      },
    }); //

    modal.present();
    const { data } = await modal.onWillDismiss();

    return data !== undefined && data.PasswordResult;
  }

  /**
   * Show modal to change current generic password (used for different puurpose)
   *
   * @param currentPassword Current password to populate field
   */
  public async showModalRequestPassword(
    currentPassword: string,
    passwordMinLenght: number = null
  ): Promise<any> {
    const modal = await this._modalController.create({
      component: PopupChangePasswordComponent,
      cssClass: ["password-change-popup", "popup-password-numeric-insert"],
      componentProps: {
        correctPassword: currentPassword,
        passwordMinLenght: passwordMinLenght,
      },
    });

    modal.present();
    const { data } = await modal.onWillDismiss();

    return data;
  }

  /**
   * Show the popup for the alarm info
   * @param alarmInfo notification
   * @param kind notification kind
   * @param gridVisible determines what kind of popup to use
   * @returns
   */
  public async showModalAlarm(
    alarmInfo: NotificationRequest_ShowPopup,
    kind: NotificationObjectKind,
    gridVisible: boolean
  ) {
    let cssClass = "";
    switch (kind) {
      case NotificationObjectKind.TypeAlarmTrue:
      case NotificationObjectKind.AlarmTrue:
      case NotificationObjectKind.LogicTrue:
        cssClass = "alarm-popup";
        break;
      case NotificationObjectKind.TypeAlarmFalse:
      case NotificationObjectKind.AlarmFalse:
      case NotificationObjectKind.LogicFalse:
        cssClass = "alarm-false-popup";
        break;
      case NotificationObjectKind.AccessControlAccessGranted:
      case NotificationObjectKind.AccessControlAccessDenied:
      case NotificationObjectKind.AccessControlAccessGrantedGuest:
      case NotificationObjectKind.AccessControlAccessDeniedGuest:
      case NotificationObjectKind.AccessControlCardInsertedAccessGrantedGuest:
      case NotificationObjectKind.AccessControlCardRemovedAccessGrantedGuest:
      case NotificationObjectKind.AccessControlCardInsertedAccessDeniedGuest:
      case NotificationObjectKind.AccessControlAccessGrantedOther:
      case NotificationObjectKind.AccessControlAccessDeniedOther:
      case NotificationObjectKind.AccessControlCardInsertedAccessGrantedOther:
      case NotificationObjectKind.AccessControlCardRemovedAccessGrantedOther:
      case NotificationObjectKind.AccessControlCardInsertedAccessDeniedOther:
      case NotificationObjectKind.AccessControlExpiringCards:
        cssClass = "access-popup";
        break;
      default:
        break;
    }
    this._alarmInfo.next(null);
    this._alarmInfo.next(alarmInfo);
    const modalPresent = document.getElementsByTagName("ion-modal")[0];
    let classes = ["generic-popup-custom", cssClass];
    if (!modalPresent && !this._modal) {
      let componentToUse: any = PopupAlarmComponent;
      if (!gridVisible) {
        classes = ["compact-alarm-popup", cssClass];
        componentToUse = GridPopupAlarmComponent;
      }
      this._modal = await this._modalController.create({
        component: componentToUse,
        cssClass: classes,
        componentProps: {
          alarmInfo: this._update,
        },
        backdropDismiss: false,
      });
      this._modal.present();
      const { data } = await this._modal.onWillDismiss();
      this._modal = null;
      return data;
    }
  }

  /**
   * Handle show modal fast checkin
   *
   * @param defaultRoom Default room to sed
   * @returns
   */
  public async showModalFastCheckin(defaultRoom: number): Promise<any> {
    const modal = await this._modalController.create({
      cssClass: ["generic-popup-custom"],
      component: PopupFastCheckinComponent,
      componentProps: {
        defaultRoom,
      },
    });

    modal.present();
    const { data } = await modal.onWillDismiss();

    return data;
  }

  /**
   * Show modal allowing the user to select which Action shold be executed on a given Address, chosen from the complete list of
   * appliable Actions for that peculiar Address. Also, in case where 'WRITE' Action is selected, open prompt for user to specify the Value to write.
   * @param address The Address to which the selected Action shall be applied; usually, it is a CommandAddress of some sort.
   * @param methodRead Boolean flag; if it is TRUE, then the command 'READ' shall be added to the list of possible Action executable
   * on the given Address.
   * @param hiLevelDataFormat The Hig Level Data Format associated to the given Address; this is used to restrict the kind of values it would
   * be possible to write on it, therefore restricting the kind of value that the user could specify if the Action 'WRITE' is selected.
   * @returns An object containing the code of the Action selected from the main Pop-up and the Value which accompany
   * this Action (usually, this Value shall be publish on some Command Address). If the Action is NOT '#WRITE', this Value
   * shall be equal to the code of the selected Action itself.
   */
  public async showModalAddressMethodsAndOpenPrompt(
    address: Address,
    methodRead,
    hiLevelDataFormat
  ) {
    let listMethods: Array<AddressMethod> = address.SplitMethods(
      address.loadMethodStringByLanguage(this._translateService.getCurrentLang())
    );
    let dataReturned = {
      action: "",
      value: "",
    };
    let title = this._lbltitleModal;
    let subTitle = this._lblSubtitleModal;
    let valueToWrite: string = "";

    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "showModalAddressMethodsAndPublishAddress() ",
      "START"
    );

    if (methodRead) {
      listMethods = [
        ...listMethods,
        {
          name: this._lblRead,
          value: ClickActionType.READ.toString(),
        },
      ];
    }
    const isWriteInList = listMethods.find((obj) => {
      return obj.value === ClickActionType.WRITE;
    });
    if (!isWriteInList) {
      for (let i = 0; i < listMethods.length; i++) {
        if (listMethods[i].value === undefined) {
          listMethods[i].value = ClickActionType.WRITE;
        }
      }
    }

    const modal = await this._modalController.create({
      cssClass: ["generic-popup-custom", "popup-address-methods-small"],
      component: PopupContentAddressMethodsComponent,
      componentProps: {
        title,
        subTitle,
        listMethods,
      },
    });

    modal.present();
    dataReturned.action = await (await modal.onWillDismiss()).data;

    if (dataReturned.action == ClickActionType.WRITE) {
      const resultOperation = await this._alertService.presentPrompt(
        this._lblWrite,
        "",
        this._lblValueToWrite,
        hiLevelDataFormat,
        true
      );
      if (resultOperation !== false) {
        valueToWrite = resultOperation.toString();
        dataReturned.value = valueToWrite;
      } else {
        dataReturned.action = "";
      }
    } else {
      dataReturned.value = dataReturned.action;
    }

    return dataReturned;
  }

  /**
   * Show modal to click address methods
   *
   * @param title Popup title
   * @param subTitle Popup subtitle
   * @param listMethods List methods to render inside popup
   * @returns
   */
  public async showModalAddressMethods(
    title: string,
    subTitle: string,
    listMethods: Array<AddressMethod>
  ): Promise<any> {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "showModalAddressMethods() ",
      "START"
    );

    const modal = await this._modalController.create({
      cssClass: ["generic-popup-custom", "popup-address-methods-smallest"],
      component: PopupContentAddressMethodsComponent,
      componentProps: {
        title,
        subTitle,
        listMethods,
      },
    });

    //WHERE:
    //54 px: dimension of each ionic-button detailing a particular action on address
    //80 px: dimension of sub-title of popup
    //17 px: dimension of title of popup (IE. "Operation")
    const effectiveHeight: number = listMethods.length * 54 + 80 + 17 + 10;
    const popupHeight: string = effectiveHeight.toString() + "px";

    modal.style.setProperty(
      "--content-address-method-popup-height",
      popupHeight
    );

    modal.present();
    const { data } = await modal.onWillDismiss();

    return data;
  }

  /**
   * Open popup with selectable list notification based on filter type
   *
   * @param title Popup title
   * @param subTitle Popup subtitle
   * @param showWithAudio Show notification with "WithAudio" equal to true
   * @param showWithBalloon Show notification with "WithBalloon" equal to true
   * @param showWithMail Show notification with "WithMail" equal to true
   * @param showWithPageJump Show notification with "WithPageJump" equal to true
   * @param showWithPopup Show notification with "WithPopup" equal to true
   * @param showWithSMS Show notification with "WithSMS" equal to true
   * @returns
   */
  public async showModalListNotification(
    title: string,
    subTitle: string,
    showWithAudio: boolean,
    showWithBalloon: boolean,
    showWithMail: boolean,
    showWithPageJump: boolean,
    showWithPopup: boolean,
    showWithSMS: boolean
  ): Promise<any> {
    const modal = await this._modalController.create({
      cssClass: ["generic-popup-custom", "popup-list-selectable"],
      component: PopupContentListNotificationComponent,
      componentProps: {
        title,
        subTitle,
        showWithAudio,
        showWithBalloon,
        showWithMail,
        showWithPageJump,
        showWithPopup,
        showWithSMS,
      },
    });

    modal.present();
    const { data } = await modal.onWillDismiss();

    return data;
  }

  /**
   * Open a popup to search for the device to associate with an existing device
   * @param isSelectedAddDevice boolean
   * @param idArea ID selected area
   * @param idEnvironment environment ID
   * @param listDevices Array of response data from getting the list of devices of that area
   * @param listKnownDevices Array of known devices
   * @returns
   */
  public async showModalAssociateSinapsiDevice(
    isSelectedAddDevice: boolean,
    idArea: number,
    idEnvironment: number,
    listDevices: Device[],
    listKnownDevices: iDevicesKnown[]
  ): Promise<any> {
    const modal = await this._modalController.create({
      cssClass: "form-modal",
      component: PopupAssociateExistingSinapsiDevice,
      componentProps: {
        isSelectedAddDevice,
        idArea,
        idEnvironment,
        listDevices,
        listKnownDevices,
      },
    });

    modal.present();
    const { data } = await modal.onWillDismiss();

    return data;
  }

  /**
   * Opens a popup for connect box management
   * @param currentIdArea Building id
   * @param idEnvironment current environment id
   * @returns
   */
  public async showModalConnectBoxManagement(
    currentIdArea: number,
    idEnvironment: number,
    isConfigurationData?: boolean,
    configurationData?: iConnectBoxConfigurationResponse
  ): Promise<any> {
    const modal = await this._modalController.create({
      cssClass: ["custom-modal-class", "form-modal"],
      component: PopupConnectBoxManagementComponent,
      componentProps: {
        currentIdArea,
        idEnvironment,
        isConfigurationData,
        configurationData,
      },
    });
    modal.present();

    const { data } = await modal.onWillDismiss();
    return data;
  }
}
